import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserPlus, Trash2, Shield, Users as UsersIcon, FileText, Loader2, Key } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AdminUsers = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '', role: 'admin', email: '' });
  const [editPasswordAdmin, setEditPasswordAdmin] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      toast.error('Super admin access required');
      navigate('/admin');
      return;
    }
    fetchAdmins();
    fetchAuditLogs();
  }, [user]);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (error) {
      toast.error('Failed to fetch admins');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs');
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!newAdmin.username || !newAdmin.password) {
      toast.error('Username and password are required');
      return;
    }

    try {
      await axios.post(`${API}/admin/users`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Admin created successfully');
      setIsDialogOpen(false);
      setNewAdmin({ username: '', password: '', role: 'admin', email: '' });
      fetchAdmins();
      fetchAuditLogs();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : Array.isArray(errorDetail) 
          ? errorDetail.map(e => e.msg || e).join(', ')
          : 'Failed to create admin';
      toast.error(errorMessage);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.put(
        `${API}/admin/users/${editPasswordAdmin}/password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Password updated successfully');
      setIsPasswordDialogOpen(false);
      setEditPasswordAdmin(null);
      setNewPassword('');
      fetchAuditLogs();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : Array.isArray(errorDetail) 
          ? errorDetail.map(e => e.msg || e).join(', ')
          : 'Failed to update password';
      toast.error(errorMessage);
    }
  };

  const handleDeleteAdmin = async (username) => {
    if (!window.confirm(`Are you sure you want to delete admin: ${username}?`)) return;

    try {
      await axios.delete(`${API}/admin/users/${username}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Admin deleted successfully');
      fetchAdmins();
      fetchAuditLogs();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : Array.isArray(errorDetail) 
          ? errorDetail.map(e => e.msg || e).join(', ')
          : 'Failed to delete admin';
      toast.error(errorMessage);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-semibold text-slate-900 mb-2">
              Admin Management
            </h1>
            <p className="text-slate-600">Manage admin users and view audit logs</p>
          </div>
          <Button onClick={() => navigate('/admin')} variant="outline">
            ← Back to Dashboard
          </Button>
        </div>

        <Tabs defaultValue="admins">
          <TabsList className="grid w-full max-w-md grid-cols-2 h-auto gap-2 bg-transparent p-0 mb-8">
            <TabsTrigger
              value="admins"
              className="rounded-lg border-2 border-slate-200 data-[state=active]:border-primary-700 data-[state=active]:bg-primary-50 py-3"
            >
              <UsersIcon className="h-4 w-4 mr-2" />
              Admin Users
            </TabsTrigger>
            <TabsTrigger
              value="audit"
              className="rounded-lg border-2 border-slate-200 data-[state=active]:border-primary-700 data-[state=active]:bg-primary-50 py-3"
            >
              <FileText className="h-4 w-4 mr-2" />
              Audit Trail
            </TabsTrigger>
          </TabsList>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <div className="mb-4">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary-700 hover:bg-primary-900">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add New Admin
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Admin</DialogTitle>
                    <DialogDescription>
                      Add a new admin user. They will be required to change their password on first login.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateAdmin} className="space-y-4">
                    <div>
                      <Label>Username *</Label>
                      <Input
                        value={newAdmin.username}
                        onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                        placeholder="Enter username"
                        required
                      />
                    </div>
                    <div>
                      <Label>Email (Optional - for password recovery)</Label>
                      <Input
                        type="email"
                        value={newAdmin.email}
                        onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div>
                      <Label>Password *</Label>
                      <Input
                        type="password"
                        value={newAdmin.password}
                        onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                        placeholder="Enter temporary password"
                        required
                      />
                    </div>
                    <div>
                      <Label>Role *</Label>
                      <Select 
                        value={newAdmin.role}
                        onValueChange={(value) => setNewAdmin({ ...newAdmin, role: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-primary-700 hover:bg-primary-900">
                        Create Admin
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {admins.map((admin) => (
                <Card key={admin.id}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          admin.role === 'super_admin' ? 'bg-purple-100' : 'bg-primary-50'
                        }`}>
                          <Shield className={`h-6 w-6 ${
                            admin.role === 'super_admin' ? 'text-purple-700' : 'text-primary-700'
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">
                            {admin.username}
                            {admin.username === user.username && (
                              <span className="ml-2 text-xs text-slate-500">(You)</span>
                            )}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-slate-600 mt-1">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              admin.role === 'super_admin' 
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-primary-100 text-primary-700'
                            }`}>
                              {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                            </span>
                            {admin.first_login && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                Must Change Password
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Created: {formatDate(admin.created_at)}
                            {admin.created_by && ` by ${admin.created_by}`}
                          </p>
                        </div>
                      </div>
                      {admin.username !== user.username && (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditPasswordAdmin(admin.username);
                              setIsPasswordDialogOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-700"
                            data-testid={`edit-password-${admin.username}`}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteAdmin(admin.username)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-admin-${admin.username}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Audit Trail Tab */}
          <TabsContent value="audit">
            <div className="space-y-3">
              {auditLogs.map((log) => (
                <Card key={log.id} className="border-l-4 border-l-primary-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            log.action.includes('FAILED') || log.action.includes('DELETED')
                              ? 'bg-red-100 text-red-700'
                              : log.action.includes('SUCCESS') || log.action.includes('CREATED')
                              ? 'bg-green-100 text-green-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {log.action}
                          </span>
                          <span className="text-sm font-medium text-slate-900">{log.performed_by}</span>
                        </div>
                        <p className="text-sm text-slate-600">{log.details}</p>
                        {log.target_user && (
                          <p className="text-xs text-slate-500 mt-1">Target: {log.target_user}</p>
                        )}
                      </div>
                      <span className="text-xs text-slate-500 whitespace-nowrap ml-4">
                        {formatDate(log.timestamp)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Password Edit Dialog */}
        <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Admin Password</DialogTitle>
              <DialogDescription>
                Set a new password for <strong>{editPasswordAdmin}</strong>. They will be required to change it on next login.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <Label>New Password *</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsPasswordDialogOpen(false);
                    setEditPasswordAdmin(null);
                    setNewPassword('');
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-700 hover:bg-blue-900">
                  <Key className="mr-2 h-4 w-4" />
                  Update Password
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminUsers;
